import React, { useContext, useEffect } from 'react';
import {
  Avatar,
  Button,
  FluentProvider,
  mergeClasses,
  useRestoreFocusTarget,
  webLightTheme,
} from '@fluentui/react-components';
import { AlertRegular, QuestionCircleRegular } from '@fluentui/react-icons';
import { useLocation } from 'react-router-dom';
import HelpPanel from '../headers/HelpPanel';
import NotificationContext from '../../notifications/NotificationContext';
import { NotificationStatus, NotificationStatusType } from '../../notifications/StatNotification';
import { IGraphUserObject } from '../../../utils/types/Users';
import ToastContainer from '../ToastContainer';
import StatHeaderSearch from './StatHeaderSearch';
import HeaderIconButton from './HeaderIconButton';
import useHeaderPanel, { OpenHeaderPanel } from './useHeaderPanel';
import StatHeaderPanel from './panels/StatHeaderPanel';
import StatProfilePanel from './panels/StatProfilePanel';
import StatNotificationPanel from './panels/StatNotificationPanel';
import useStatHeaderStyles from './StatHeader.styles';

interface IStatHeaderProps {
  onLogOut: () => void;
  graphProfileData: IGraphUserObject | null;
  imageUrl: string | null;
  environmentName?: string;
}

/**
 * Site header.
 *
 * Responsive layout is handled exclusively by the media queries in `StatHeader.styles.ts`.
 * This component never measures the viewport: the previous implementation derived
 * `isMobile` from `window.innerWidth` and forked its render tree three ways from it, which
 * meant every layout change had to be mirrored back into React state (and regularly went
 * out of sync).
 */
const StatHeader = ({
  onLogOut,
  graphProfileData,
  imageUrl,
  environmentName,
}: IStatHeaderProps): JSX.Element => {
  const styles = useStatHeaderStyles();
  const location = useLocation();
  const restoreFocusTargetAttributes = useRestoreFocusTarget();
  const { openPanel, activePanel, isOpen, toggle, close } = useHeaderPanel();
  useEffect(() => {
    close();
  }, [location, close]);
  const { notifications, setNotifications } = useContext(NotificationContext);

  const isProd = !environmentName;
  const name = graphProfileData?.displayName ?? '';
  const email = graphProfileData?.mail?.toLowerCase() ?? '';
  const title = isProd ? 'Microsoft Stat' : `Microsoft Stat - ${environmentName}`;

  const unreadCount = notifications.filter(
    (notification) => notification.notificationStatus === NotificationStatus.Unread,
  ).length;

  const notificationLabel =
    unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications';

  const updateNotification = (itemKey: string, status: NotificationStatusType): void => {
    setNotifications(
      notifications.map((notification) =>
        notification.itemKey === itemKey
          ? { ...notification, notificationStatus: status }
          : notification,
      ),
    );
  };

  /**
   * All three panels share one `StatHeaderPanel` instance rather than rendering a drawer
   * each, which matches the mutually-exclusive model the reducer already encodes: switching
   * panels swaps the drawer's contents instead of unmounting one modal and mounting another
   * in the same commit. Note that the drawer then stays mounted across a swap, so Fluent's
   * open-time focus move does not re-run — `StatHeaderPanel` compensates via `contentKey`.
   */
  const panelContent: Record<OpenHeaderPanel, { title: string; body: JSX.Element }> = {
    notifications: {
      title: 'Notifications',
      body: (
        <StatNotificationPanel
          notifications={notifications}
          onUpdateNotification={updateNotification}
        />
      ),
    },
    help: { title: 'Help', body: <HelpPanel /> },
    profile: {
      title: 'Account',
      body: (
        <StatProfilePanel
          name={name}
          email={email}
          imageUrl={imageUrl ?? undefined}
          onLogOut={onLogOut}
        />
      ),
    },
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <div
        role="banner"
        className={mergeClasses(styles.root, !isProd && styles.rootNonProd)}
        data-environment={environmentName || 'prod'}
      >
        <a href="/" className={styles.titleLink} aria-label={title}>
          <span className={styles.titleText} aria-hidden>
            Microsoft Stat
            {!isProd && <span className={styles.titleEnvironment}> - {environmentName}</span>}
          </span>
        </a>

        <StatHeaderSearch />

        <div className={styles.actions}>
          <HeaderIconButton
            icon={<AlertRegular fontSize={20} />}
            label={notificationLabel}
            badgeCount={unreadCount}
            checked={isOpen('notifications')}
            onClick={() => toggle('notifications')}
          />
          <HeaderIconButton
            icon={<QuestionCircleRegular fontSize={20} />}
            label="Help"
            checked={isOpen('help')}
            onClick={() => toggle('help')}
          />
          <Button
            {...restoreFocusTargetAttributes}
            className={mergeClasses(
              styles.actionButton,
              styles.avatarButton,
              isOpen('profile') && styles.actionButtonChecked,
            )}
            appearance="subtle"
            aria-label={name ? `Profile, ${name}` : 'Profile'}
            aria-pressed={isOpen('profile')}
            title={name ? `Profile, ${name}` : 'Profile'}
            onClick={() => toggle('profile')}
          >
            <span className={styles.avatarWrapper}>
              <Avatar
                image={{ src: imageUrl ?? undefined }}
                name={name}
                color="colorful"
                size={32}
              />
            </span>
          </Button>
        </div>
      </div>

      <ToastContainer />

      <StatHeaderPanel
        open={openPanel !== 'none'}
        title={panelContent[activePanel].title}
        contentKey={activePanel}
        onClose={close}
      >
        {panelContent[activePanel].body}
      </StatHeaderPanel>
    </FluentProvider>
  );
};

export default StatHeader;
